/**
 * engrase.ts — IS Engrase block builder for pdfmake.
 *
 * Renders the engrase (lubrication system) block that appears after §4 in IS reports
 * when tipoServicio === "engrase".
 *
 * Structure:
 *   - Sub-heading: "REGULACIÓN DE TEMPORIZADOR Y PRESIÓN DE TRABAJO (SISTEMA DE ENGRASE)"
 *   - 2-col centered table: Tiempo de reposo | Tiempo de lubricación
 *   - 3-col centered table: Tipo de presión | Presión de trabajo | Presión de lubricación
 *   - Sub-heading: "REGULACIÓN DE DOSIFICADORES (SISTEMA DE ENGRASE)"
 *   - Per-zona:
 *       zone title (underlined, primary color)
 *       optional description paragraph
 *       generic table from zona.tabla.columnas/filas (centered)
 *
 * Tables use IS-style: navy header row, NO navy first-column in body (all body cells white/zebra),
 * centered body text. This matches old body.ts engrase styling (unnumbered sub-sections, centered).
 */
import type { Content, ContentTable } from "pdfmake/interfaces.js";
import type { VmEngrase, Theme } from "../types.js";
import { autoContrast, readableOn, subtleBorder, zebraFill } from "./color.js";

const BORDER_COLOR = subtleBorder("#ffffff");

/**
 * IS-style unnumbered sub-heading: primary color, bold, no numeric prefix.
 * Used for engrase section titles (matches old body.ts subheading()).
 */
function engraseSubheading(title: string, theme: Theme): Content {
  return {
    text: title,
    fontSize: 9,
    bold: true,
    // Brand color as text on the white page → keep readable if primary is pale.
    color: readableOn("#ffffff", theme.primary),
    margin: [0, 10, 0, 4],
  } as Content;
}

/**
 * Zone title: underlined primary-colored text (LNF-01 / TBL-01).
 * Used for per-zone sub-titles within the dosificadores section.
 */
function zonaTitle(title: string, theme: Theme): Content {
  return {
    text: title,
    fontSize: 9,
    bold: true,
    color: readableOn("#ffffff", theme.primary),
    decoration: "underline",
    margin: [0, 6, 0, 3],
  } as Content;
}

/**
 * Build a centered engrase table.
 *
 * Unlike buildTable (which always navies col-0), this table has:
 *   - Navy header row (all columns, white text) — matching brand style
 *   - Body rows: all cells white/zebra alternating — NO navy first column
 *   - Centered alignment on all cells
 *   - Equal-width columns ("*" for each)
 *
 * @param head  - column header labels
 * @param rows  - body rows (string arrays)
 * @param theme - brand theme
 */
function buildEngraseTable(head: string[], rows: string[][], theme: Theme): ContentTable {
  const widths: string[] = Array(head.length).fill("*") as string[];

  const onPrimary = autoContrast(theme.primary);
  const zebra = zebraFill("#ffffff", theme.primary);
  const headerRow = head.map((h) => ({
    text: h,
    bold: true,
    color: onPrimary,
    fontSize: 9,
    alignment: "center" as const,
  }));

  const bodyRows = rows.map((row) =>
    row.map((cell) => ({
      text: cell,
      color: "#333333",
      fontSize: 9,
      alignment: "center" as const,
    })),
  );

  return {
    table: {
      headerRows: 1,
      dontBreakRows: true,
      keepWithHeaderRows: 1,
      widths,
      body: [headerRow, ...bodyRows],
    },
    layout: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fillColor: (rowIndex: number, node: any) => {
        const hRows: number = Number(node?.table?.headerRows ?? 1);
        if (rowIndex < hRows) return theme.primary;
        const bodyIdx = rowIndex - hRows;
        return bodyIdx % 2 === 1 ? zebra : null;
      },
      hLineWidth: () => 0.5,
      hLineColor: () => BORDER_COLOR,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      vLineWidth: (i: number, node: any) => {
        const cols = (node.table.widths as unknown[]).length;
        return i === 0 || i === cols ? 0.5 : 0;
      },
      vLineColor: () => BORDER_COLOR,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
    margin: [0, 0, 0, 8],
  } as ContentTable;
}

/**
 * Build the IS engrase block (renders after §4 when tipoServicio === "engrase").
 *
 * @param engrase - VmEngrase data from the InformeVM
 * @param theme   - brand theme
 */
export function buildEngrase(engrase: VmEngrase, theme: Theme): Content {
  const stack: Content[] = [];

  // Sub-heading 1: timer/pressure regulation
  stack.push(engraseSubheading(
    "REGULACIÓN DE TEMPORIZADOR Y PRESIÓN DE TRABAJO (SISTEMA DE ENGRASE)",
    theme,
  ));

  // 2-col timer table: Tiempo de reposo | Tiempo de lubricación
  stack.push(buildEngraseTable(
    ["Tiempo de reposo", "Tiempo de lubricación"],
    [[engrase.tiempoReposo, engrase.tiempoLubricacion]],
    theme,
  ));

  // 3-col pressure table: Tipo de presión | Presión de trabajo | Presión de lubricación
  stack.push(buildEngraseTable(
    ["Tipo de presión", "Presión de trabajo", "Presión de lubricación"],
    [[engrase.tipoPresion, engrase.presionTrabajo, engrase.presionLubricacion]],
    theme,
  ));

  // Sub-heading 2: dosificadores
  stack.push(engraseSubheading("REGULACIÓN DE DOSIFICADORES (SISTEMA DE ENGRASE)", theme));

  // Per-zona tables
  for (const zona of engrase.zonas) {
    // Zone title (underlined + primary)
    stack.push(zonaTitle(zona.titulo || "ZONA", theme));

    // Optional zone description paragraph
    if (zona.descripcion) {
      stack.push({
        text: zona.descripcion,
        fontSize: 8.5,
        color: "#333333",
        margin: [0, 2, 0, 4],
      } as Content);
    }

    // Generic per-zona table from columnas/filas.
    // Always render at least one body row so we never produce a header-only table
    // (a header-only table can appear as an orphan at the bottom of a page).
    // When filas is empty, show a single "—" marker row.
    const { columnas, filas } = zona.tabla;
    if (columnas.length > 0) {
      const head = columnas.map((c) => c.titulo);
      const rows =
        filas.length > 0
          ? filas.map((fila) => columnas.map((col) => String(fila[col.id] ?? "")))
          : [columnas.map((_, i) => (i === 0 ? "—" : ""))]; // "—" marker row when no data
      stack.push(buildEngraseTable(head, rows, theme));
    }
  }

  return { stack, margin: [0, 0, 0, 12] } as Content;
}
